import {
  baseBitcoinYayURL,
  baseNewCEXURL,
  baseWSURL,
} from "../../../services/api";

/* Product logos — the dark-mode icon set in assets/indexxNewIcons. All nine are
   120x120 squares, which is why the tiles can share one fixed media box. */
import aiAiNAiLogo from "../../../assets/indexxNewIcons/AistationLogo.webp";
import bitcoinYayLogo from "../../../assets/indexxNewIcons/bitcoinyayLogo.webp";
import cryptoTreasuryLogo from "../../../assets/indexxNewIcons/CryptoTreasuryLogo.webp";
import emmmLogo from "../../../assets/indexxNewIcons/EmmmLogo.webp";
import exchangeLogo from "../../../assets/indexxNewIcons/ExchangeLogo.webp";
import usdxxLogo from "../../../assets/indexxNewIcons/UsdxxLogo.webp";
import wallstreetLogo from "../../../assets/indexxNewIcons/wallstreetLogo.webp";
import xTokensLogo from "../../../assets/indexxNewIcons/XCryptoLogo.webp";
import yaysAppLogo from "../../../assets/indexxNewIcons/YaysAppLogo.webp";

export interface EcosystemProduct {
  id: string;
  name: string;
  category: string;
  categoryTag: "AI & Agents" | "Exchange & DEX" | "Gaming & Treasury" | "Education & Utilities";
  desc: string;
  image: string;
  ctaText: string;
  url: string;
  isSoon?: boolean;
}

export interface ArchitecturePillar {
  id: string;
  title: string;
  desc: string;
  icon: string;
  checkLabel: string;
}

export interface MetricItem {
  value: string;
  label: string;
}

export const ECOSYSTEM_PRODUCTS: EcosystemProduct[] = [
  {
    id: "ai-ai-n-ai",
    name: "Ai Ai N Ai",
    category: "AI Agent Layer",
    categoryTag: "AI & Agents",
    desc: "Global AI agent network for high performance operations.",
    image: aiAiNAiLogo,
    ctaText: "Deploy AI Agents",
    url: "https://aiainai.ai/",
  },
  {
    id: "bitcoin-yay",
    name: "Bitcoin Yay",
    category: "Gamified Yield",
    categoryTag: "Gaming & Treasury",
    desc: "Bitcoin Yay is the micro token and petty cash of Bitcoin.",
    image: bitcoinYayLogo,
    ctaText: "Explore Bitcoin Yay",
    url: baseBitcoinYayURL,
  },
  {
    id: "crypto-treasury",
    name: "Crypto Treasury",
    category: "Reserve Asset",
    categoryTag: "Gaming & Treasury",
    desc: "Drives crypto value through security, innovation, and growth.",
    image: cryptoTreasuryLogo,
    ctaText: "View Treasury Reserves",
    url: "https://cex.indexx.ai/crypto-treasury",
  },
  {
    id: "eeny-meeny-miny-moe",
    name: "Eeny Meeny Miny Moe",
    category: "Prediction Markets",
    categoryTag: "Gaming & Treasury",
    desc: "Eeny Meeny Miny Moe premium global prediction market.",
    image: emmmLogo,
    ctaText: "Play & Predict",
    url: "https://eenymeenyminymoe.io/",
  },
  {
    id: "exchange",
    name: "Exchange",
    category: "DEX & Orderbook",
    categoryTag: "Exchange & DEX",
    desc: "Crypto exchange where you buy, sell and convert cryptocurrencies.",
    image: exchangeLogo,
    ctaText: "Trade on Exchange",
    url: baseNewCEXURL,
  },
  {
    id: "usdxx",
    name: "USDXX",
    category: "Stable Settlement",
    categoryTag: "Exchange & DEX",
    desc: "A digital finance platform built around USDXX for seamless digital asset transactions and financial services.",
    image: usdxxLogo,
    ctaText: "Coming Soon",
    url: "https://usdxlynxx.com",
    isSoon: true,
  },
  {
    id: "wall-street",
    name: "Wallstreet",
    category: "Synthetic Finance",
    categoryTag: "Exchange & DEX",
    desc: "Market exchange for small businesses.",
    image: wallstreetLogo,
    ctaText: "Access Wallstreet",
    url: baseWSURL,
  },
  {
    id: "xtokens",
    name: "xTokens",
    category: "Governance",
    categoryTag: "Exchange & DEX",
    desc: "Dive into all the tokens made by Indexx.ai.",
    image: xTokensLogo,
    ctaText: "Stake xTokens",
    url: "/xtokens",
  },
  {
    id: "yaysapp",
    name: "YaysApp",
    category: "Social Web3",
    categoryTag: "AI & Agents",
    desc: "A social and community platform for connecting, sharing, and engaging within the Yay ecosystem.",
    image: yaysAppLogo,
    ctaText: "Join Communities",
    url: "https://web.yaysapp.com",
  },
];

/* "Native Platforms" counts the suite itself, so it cannot drift out of date
   when a product is added or pulled. Declared after ECOSYSTEM_PRODUCTS because
   it reads its length at module init. */
export const ECOSYSTEM_METRICS: MetricItem[] = [
  { value: "$420M+", label: "Ecosystem TVL" },
  { value: String(ECOSYSTEM_PRODUCTS.length), label: "Native Platforms" },
  { value: "1.2M+", label: "Active Wallets" },
  { value: "99.99%", label: "Proof of Uptime" },
];

export const ARCHITECTURE_PILLARS: ArchitecturePillar[] = [
  {
    id: "pillar-1",
    title: "Unified Indexx ID & Wallet",
    desc: "One single decentralized identifier allows instantaneous access across Exchange, Wallstreet, and Crypto Treasury without re-authenticating or fragmenting your assets.",
    icon: "fingerprint",
    checkLabel: "Passkey & MPC Ready",
  },
  {
    id: "pillar-2",
    title: "Shared Protocol Liquidity",
    desc: "Crypto Treasury reserves and Exchange orderbook depth feed capital directly into prediction market liquidity and stable settlement, maximizing capital efficiency.",
    icon: "account_balance",
    checkLabel: "Zero-Slippage Routing",
  },
  {
    id: "pillar-3",
    title: "Cross-Ecosystem AI Core",
    desc: "The neural inference models born in Ai Ai N Ai actively inspect volatility, adjust synthetic collateral rates on WallStreet, and forecast risk in real time.",
    icon: "neurology",
    checkLabel: "Sub-second Machine Inference",
  },
];

export const FOOTER_DIRECTORY = {
  platform: [
    { label: "Academy", url: "/indexx-exchange/how-it-works" },
    { label: "Exchange / Buy Crypto", url: "/indexx-exchange/buy-sell/crypto" },
    { label: "Fantasy Lotto", url: "/super-ball/airdrop" },
    { label: "Hive", url: "/indexx-exchange/how-it-works/hive" },
    { label: "Shop", url: "/discount-shop" },
  ],
  products: [
    { label: "Gift Cards", url: "/digital-products-info" },
    { label: "Greeting Cards", url: "/digital-products-info" },
    { label: "xTokens", url: "/xtokens" },
    { label: "Bitcoin Yay", url: "https://bitcoinyay.com" },
    { label: "WallStreet", url: "/indexx-exchange/token-details/in500" },
  ],
  program: [
    { label: "Affiliate Program", url: "/indexx-exchange/legal/affiliateprogram" },
    { label: "Hive", url: "/indexx-exchange/how-it-works/hive" },
  ],
  wallet: [
    { label: "Wallet Extension", url: "/indexx-exchange/buy-sell/wallet" },
    { label: "Wallet Web", url: "/indexx-exchange/buy-sell/wallet" },
  ],
  company: [
    { label: "About", url: "/indexx-exchange/about" },
    { label: "Back to Community", url: "/back-to-community" },
    { label: "Blog & Careers", url: "/indexx-exchange/blog" },
    { label: "Documents", url: "/indexx-exchange/legal" },
    { label: "Elite Club", url: "/elite-club" },
    { label: "How It Works", url: "/indexx-exchange/how-it-works" },
  ],
};
